import './klantvertellen.scss';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin( Draggable, InertiaPlugin );

function initKlantvertellen() {
	const roots = document.querySelectorAll( '[data-component="klantvertellen"]' );

	if ( ! roots.length ) {
		return;
	}

	roots.forEach( ( root ) => {
		const slider = root.querySelector( '[data-klantvertellen-slider]' );
		const track = root.querySelector( '[data-klantvertellen-track]' );
		const prevBtn = root.querySelector( '.b-klantvertellen__navBtn.-prev' );
		const nextBtn = root.querySelector( '.b-klantvertellen__navBtn.-next' );

		if ( ! slider || ! track ) {
			return;
		}

		let draggableInstance = null;

		const getCardWidth = () => {
			const card = track.querySelector( '.b-klantvertellen__card' );
			const gapStr = window.getComputedStyle( track ).gap;
			const gap = parseFloat( gapStr ) || 0;

			if ( card ) {
				return card.offsetWidth + gap;
			}
			return 600;
		};

		const getBounds = () => {
			const sliderWidth = slider.offsetWidth;
			const trackWidth = track.scrollWidth;
			const minX = Math.min( 0, sliderWidth - trackWidth );
			return { minX: minX, maxX: 0 };
		};

		const draggables = Draggable.create( track, {
			trigger: slider,
			type: 'x',
			inertia: true,
			resistance: 3000,
			edgeResistance: 0.65,
			bounds: getBounds,
			onPress() {
				gsap.killTweensOf( track );
			},
			snap: {
				x: ( value ) => {
					const step = getCardWidth();
					const snapped = Math.round( value / step ) * step;
					const bounds = getBounds();
					return Math.max( bounds.minX, Math.min( bounds.maxX, snapped ) );
				},
			},
		} );

		draggableInstance = draggables[ 0 ];

		const moveSlider = ( direction ) => {
			if ( ! draggableInstance ) {
				return;
			}

			const cardWidth = getCardWidth();
			const bounds = getBounds();
			const currentX = gsap.getProperty( track, 'x' ) || 0;
			let targetX = currentX + ( direction * cardWidth );

			targetX = Math.max( bounds.minX, Math.min( bounds.maxX, targetX ) );

			gsap.to( track, {
				x: targetX,
				duration: 0.6,
				ease: 'power3.out',
				onUpdate: () => {
					if ( draggableInstance ) {
						draggableInstance.update();
					}
				},
			} );
		};

		if ( prevBtn ) {
			prevBtn.addEventListener( 'click', () => moveSlider( 1 ) );
		}

		if ( nextBtn ) {
			nextBtn.addEventListener( 'click', () => moveSlider( -1 ) );
		}

		const handleResize = () => {
			if ( ! draggableInstance ) {
				return;
			}

			const bounds = getBounds();
			const currentX = gsap.getProperty( track, 'x' ) || 0;

			if ( currentX < bounds.minX ) {
				gsap.to( track, {
					x: bounds.minX,
					duration: 0.3,
					onUpdate: () => {
						draggableInstance.update();
					},
				} );
			} else if ( currentX > bounds.maxX ) {
				gsap.to( track, {
					x: bounds.maxX,
					duration: 0.3,
					onUpdate: () => {
						draggableInstance.update();
					},
				} );
			}
		};

		window.addEventListener( 'resize', handleResize );

		const LINE_CLAMP = 3;

		root.querySelectorAll( '[data-klantvertellen-card]' ).forEach( ( card ) => {
			const tekst = card.querySelector( '.b-klantvertellen__cardTekst' );
			const btn = card.querySelector( '[data-klantvertellen-leesmeer]' );
			const panel = card.querySelector( '[data-klantvertellen-panel]' );
			const panelClose = card.querySelector( '[data-klantvertellen-panel-close]' );

			if ( ! tekst || ! btn ) {
				return;
			}

			const lineHeight = parseFloat( window.getComputedStyle( tekst ).lineHeight ) || 30;
			const clampedHeight = lineHeight * LINE_CLAMP;

			gsap.set( tekst, { height: clampedHeight } );

			if ( tekst.scrollHeight <= clampedHeight ) {
				btn.style.display = 'none';
			}

			if ( ! panel ) {
				return;
			}

			let isOpen = false;
			let panelTl = null;

			const panelBg = panel.querySelector( '.b-klantvertellen__panelBg' );
			const panelContent = panel.querySelector( '.b-klantvertellen__panelContent' );

			const openPanel = () => {
				if ( isOpen ) {
					return;
				}
				isOpen = true;

				if ( panelTl ) {
					panelTl.kill();
				}

				gsap.set( panel, { visibility: 'visible', pointerEvents: 'auto' } );

				panelTl = gsap.timeline();

				panelTl.fromTo( panelBg, {
					clipPath: 'inset(0 0 100% 0)',
				}, {
					clipPath: 'inset(0 0 0% 0)',
					duration: 0.5,
					ease: 'power3.inOut',
				}, 0 );

				if ( panelContent ) {
					const children = panelContent.children;
					panelTl.fromTo( children, {
						y: 20,
						opacity: 0,
					}, {
						y: 0,
						opacity: 1,
						duration: 0.4,
						ease: 'power2.out',
						stagger: 0.08,
					}, 0.3 );
				}

				if ( panelClose ) {
					panelTl.fromTo( panelClose, {
						opacity: 0,
						rotate: -90,
					}, {
						opacity: 1,
						rotate: 0,
						duration: 0.35,
						ease: 'power2.out',
					}, 0.35 );
				}
			};

			const closePanel = () => {
				if ( ! isOpen ) {
					return;
				}

				if ( panelTl ) {
					panelTl.kill();
				}

				panelTl = gsap.timeline( {
					onComplete: () => {
						gsap.set( panel, { visibility: 'hidden', pointerEvents: 'none' } );
						isOpen = false;
					},
				} );

				if ( panelClose ) {
					panelTl.to( panelClose, {
						opacity: 0,
						rotate: 90,
						duration: 0.2,
						ease: 'power2.in',
					}, 0 );
				}

				if ( panelContent ) {
					panelTl.to( panelContent.children, {
						y: -15,
						opacity: 0,
						duration: 0.25,
						ease: 'power2.in',
						stagger: 0.04,
					}, 0 );
				}

				panelTl.to( panelBg, {
					clipPath: 'inset(100% 0 0 0)',
					duration: 0.45,
					ease: 'power3.inOut',
				}, 0.15 );
			};

			btn.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				e.stopPropagation();
				openPanel();
			} );

			if ( panelClose ) {
				panelClose.addEventListener( 'click', ( e ) => {
					e.preventDefault();
					e.stopPropagation();
					closePanel();
				} );
			}
		} );

		root._klantvertellenCleanup = () => {
			if ( draggableInstance ) {
				draggableInstance.kill();
			}
			window.removeEventListener( 'resize', handleResize );
		};
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initKlantvertellen );
} else {
	initKlantvertellen();
}
